import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Instagram, Facebook, MessageCircle, Loader2 } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { BUSINESS, API_URL } from "../config";
import { toast } from "sonner";

const inputCls =
  "w-full rounded-lg border border-[#E5E5E5] bg-white px-4 py-3 text-base text-[#1C1C1C] placeholder:text-[#A3A3A3] focus:outline-none focus:ring-2 focus:ring-[#C87A38] focus:border-transparent transition-shadow";

export default function Contact() {
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", message: "" });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const data = new FormData();
      data.append("full_name", form.full_name);
      data.append("phone", form.phone);
      data.append("email", form.email);
      data.append("suburb", "General enquiry");
      data.append("service", "General enquiry");
      data.append("message", form.message);
      await axios.post(`${API_URL}/quotes`, data);
      toast.success("Thank you for contacting Zum Flooring. We have received your flooring enquiry and will be in touch shortly.");
      setForm({ full_name: "", phone: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again or give us a call.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        label="Contact Us"
        title="Talk to Zum Flooring"
        description="Questions about hybrid flooring, pricing or availability? Get in touch — we service Melbourne and surrounding suburbs."
        testId="contact-header"
      />

      <section className="py-20 md:py-24" data-testid="contact-details-section">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-[#C87A38] shrink-0 mt-1" />
                <div>
                  <h2 className="font-semibold font-heading text-lg">Service area</h2>
                  <p className="text-base text-[#525252]" data-testid="contact-service-area">{BUSINESS.serviceArea}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="w-5 h-5 text-[#C87A38] shrink-0 mt-1" />
                <div>
                  <h2 className="font-semibold font-heading text-lg">Phone</h2>
                  <a href={BUSINESS.phoneTel} className="text-base text-[#525252] hover:text-[#C87A38] transition-colors" data-testid="contact-phone">{BUSINESS.phoneDisplay}</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="w-5 h-5 text-[#C87A38] shrink-0 mt-1" />
                <div>
                  <h2 className="font-semibold font-heading text-lg">Email</h2>
                  <a href={`mailto:${BUSINESS.email}`} className="text-base text-[#525252] hover:text-[#C87A38] transition-colors" data-testid="contact-email">{BUSINESS.email}</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock className="w-5 h-5 text-[#C87A38] shrink-0 mt-1" />
                <div>
                  <h2 className="font-semibold font-heading text-lg">Business hours</h2>
                  <ul className="text-base text-[#525252]" data-testid="contact-hours">
                    {BUSINESS.hours.map((h) => (
                      <li key={h.days} className="flex justify-between gap-6 max-w-xs">
                        <span>{h.days}</span>
                        <span>{h.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href={BUSINESS.phoneTel}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C87A38] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#A6632B] focus:outline-none focus:ring-2 focus:ring-[#C87A38] focus:ring-offset-2"
                data-testid="contact-call-button"
              >
                <Phone className="w-4 h-4" /> Call Now
              </a>
              <a
                href={BUSINESS.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#4A3018] px-6 py-3.5 text-sm font-semibold text-[#4A3018] transition-colors hover:bg-[#4A3018] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#C87A38]"
                data-testid="contact-whatsapp-button"
              >
                <MessageCircle className="w-4 h-4" /> Message on WhatsApp
              </a>
            </div>

            <div className="mt-8 flex items-center gap-4">
              <a href={BUSINESS.instagramUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm font-medium text-[#525252] hover:text-[#C87A38] transition-colors" data-testid="contact-instagram">
                <Instagram className="w-5 h-5" /> Instagram
              </a>
              <a href={BUSINESS.facebookUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm font-medium text-[#525252] hover:text-[#C87A38] transition-colors" data-testid="contact-facebook">
                <Facebook className="w-5 h-5" /> Facebook
              </a>
            </div>
          </div>

          <div className="border border-[#E5E5E5] rounded-2xl p-8 bg-[#FAFAFA]">
            <h2 className="text-2xl font-semibold font-heading">Send us a message</h2>
            <form onSubmit={submit} className="mt-6 space-y-5" data-testid="contact-form">
              <div>
                <label htmlFor="c_name" className="block text-sm font-semibold text-[#4A3018] mb-1.5">Full name <span className="text-[#C87A38]">*</span></label>
                <input id="c_name" className={inputCls} value={form.full_name} onChange={set("full_name")} required data-testid="contact-input-name" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="c_phone" className="block text-sm font-semibold text-[#4A3018] mb-1.5">Phone <span className="text-[#C87A38]">*</span></label>
                  <input id="c_phone" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} required data-testid="contact-input-phone" />
                </div>
                <div>
                  <label htmlFor="c_email" className="block text-sm font-semibold text-[#4A3018] mb-1.5">Email <span className="text-[#C87A38]">*</span></label>
                  <input id="c_email" type="email" className={inputCls} value={form.email} onChange={set("email")} required data-testid="contact-input-email" />
                </div>
              </div>
              <div>
                <label htmlFor="c_message" className="block text-sm font-semibold text-[#4A3018] mb-1.5">Message <span className="text-[#C87A38]">*</span></label>
                <textarea id="c_message" rows={4} className={inputCls} value={form.message} onChange={set("message")} required data-testid="contact-input-message" />
              </div>
              <button
                type="submit"
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#4A3018] px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#3a2512] disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[#C87A38]"
                data-testid="contact-submit-button"
              >
                {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                {sending ? "Sending…" : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-24" data-testid="contact-map-section">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-semibold font-heading mb-2">Where we work</h2>
          <p className="text-sm text-[#525252] mb-6">
            We come to you — homes across Melbourne and surrounding suburbs. (We are a mobile service, so there is no showroom address to visit.)
          </p>
          <div className="rounded-2xl overflow-hidden border border-[#E5E5E5]">
            <iframe
              title="Zum Flooring service area — Melbourne, Victoria"
              src="https://www.google.com/maps?q=Melbourne+VIC,+Australia&output=embed"
              className="w-full h-[380px]"
              loading="lazy"
              data-testid="contact-map"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
